// Utilitário para gerenciar dados mock do sistema
import logger from "./logger";

// Chave do localStorage para modo mock
const MOCK_MODE_KEY = "isMockMode";

// Constantes para dados mock
const MOCK_PLOTS = ["ABC0000", "CBA0001", "BCA0002"];

const MOCK_MUNICIPALITIES = [
  "Porto Alegre",
  "Canoas",
  "Caxias do Sul",
  "Pelotas",
  "Santa Maria",
  "Novo Hamburgo",
  "São Leopoldo",
  "Rio Grande",
  "Passo Fundo",
  "Uruguaiana"
];

const MOCK_PLAGUES = [
  { name: "Lagarta Verde", scientific: "Psorocampa Denticulata" },
  { name: "Lagarta Parda", scientific: "Thyrinteina Arnobia" },
  { name: "Lagarta Cachorrinho", scientific: "Eupseudosoma Spp" },
  { name: "Glena", scientific: "Glena Bipennaria" },
  { name: "Lagarta Marrom", scientific: "Oxydia Vesulia" },
  { name: "Sarsina", scientific: "Sarsina Spp" },
  { name: "Dyrphia", scientific: "Dyrphia Rosacordis" },
  { name: "Nistalea", scientific: "Nystalea Nyseus" },
  { name: "Hylesia", scientific: "Hylesia Sp" },
  { name: "Sabulodes", scientific: "Sabulodes Caberata" },
  { name: "Psilideo de Concha", scientific: "Glycaspis Brimblecombei" },
  { name: "Percevejo Bronzeado", scientific: "Thaumastocoris Peregrinus" },
  { name: "Besouro Amarelo", scientific: "Costalimaita Ferruginea" },
  { name: "Besouro Azul", scientific: "Sternocolaspis Quatuord" }
];

/**
 * Verifica se está em modo mock
 * @returns {boolean} True se está em modo mock
 */
export const isMockMode = () => {
  const mockMode = localStorage.getItem(MOCK_MODE_KEY);
  return mockMode === "true";
};

/**
 * Define o modo mock
 * @param {boolean} isMock - Se deve ativar modo mock
 */
export const setMockMode = (isMock) => {
  if (isMock) {
    localStorage.setItem(MOCK_MODE_KEY, "true");
    logger.info("Modo mock ativado");
  } else {
    localStorage.removeItem(MOCK_MODE_KEY);
    logger.info("Modo mock desativado");
  }
};

/**
 * Retorna as regiões mock disponíveis
 * @returns {Array} Array de regiões mock
 */
export const getMockRegions = () => {
  return [
    { value: "Norte", label: "Norte" },
    { value: "Sul", label: "Sul" }
  ];
};

/**
 * Gera um número aleatório entre min e max (inclusive)
 * @param {number} min - Valor mínimo
 * @param {number} max - Valor máximo
 * @param {number} decimals - Número de casas decimais
 * @returns {number} Número aleatório
 */
const randomNumber = (min, max, decimals = 0) => {
  const num = Math.random() * (max - min) + min;
  return parseFloat(num.toFixed(decimals));
};

/**
 * Seleciona um item aleatório de um array
 * @param {Array} array - Array para selecionar
 * @returns {*} Item aleatório
 */
const randomItem = (array) => {
  return array[Math.floor(Math.random() * array.length)];
};

/**
 * Gera um município aleatório
 * @returns {string} Município aleatório
 */
const randomMunicipality = () => {
  return randomItem(MOCK_MUNICIPALITIES);
};

/**
 * Gera uma praga aleatória
 * @returns {Object} Praga aleatória {name, scientific}
 */
const randomPlague = () => {
  return randomItem(MOCK_PLAGUES);
};

/**
 * Gera um UGB aleatório
 * @returns {string} UGB no formato "UGB1" até "UGB10"
 */
const randomUGB = () => {
  const number = Math.floor(Math.random() * 10) + 1;
  return `UGB${number}`;
};

/**
 * Gera um ID de projeto aleatório
 * @returns {string} ID no formato "PROJ1000" até "PROJ9999"
 */
const randomIdProjeto = () => {
  const number = Math.floor(Math.random() * 9000) + 1000;
  return `PROJ${number}`;
};

/**
 * Gera um nível de infestação baseado na distribuição:
 * 80% Baixo, 15% Médio, 5% Alto
 * @returns {Object} {level: string, prob: number}
 */
const generateInfestationLevel = () => {
  const random = Math.random();
  
  if (random < 0.8) {
    // 80% Baixo: prob entre 15.00 e 30.00
    return {
      level: "Baixo",
      prob: randomNumber(15.0, 30.0, 2)
    };
  } else if (random < 0.95) {
    // 15% Médio: prob entre 30.10 e 50.00
    return {
      level: "Médio",
      prob: randomNumber(30.1, 50.0, 2)
    };
  } else {
    // 5% Alto: prob entre 50.10 e 95.00
    return {
      level: "Alto",
      prob: randomNumber(50.1, 95.0, 2)
    };
  }
};

/**
 * Gera talhões mock para uma região
 * @param {string} region - Região ("Norte" ou "Sul")
 * @returns {Array} Array de 3 talhões mock
 */
export const generateMockPlots = (region) => {
  const plots = MOCK_PLOTS.map((plotNumber, index) => {
    return {
      plot: plotNumber,
      region: region,
      municipality: randomMunicipality(),
      area: randomNumber(50.0, 300.0, 2).toString(),
      material: "Eucalyptus",
      rotation: Math.floor(Math.random() * 7) + 1,
      idade: Math.floor(Math.random() * 10) + 1,
      ugb: randomUGB(),
      id_projeto: randomIdProjeto()
    };
  });

  logger.debug("Talhões mock gerados", {
    region,
    count: plots.length
  });

  return plots;
};

/**
 * Gera uma previsão mock
 * @param {string} region - Região ("Norte" ou "Sul")
 * @param {number} index - Índice da previsão (para rotacionar talhões)
 * @returns {Object} Previsão mock
 */
const generateMockPrediction = (region, index) => {
  // Rotaciona entre os 3 talhões
  const plotIndex = index % MOCK_PLOTS.length;
  const plot = MOCK_PLOTS[plotIndex];
  
  const plague = randomPlague();
  const infestation = generateInfestationLevel();

  return {
    plot: plot,
    region: region,
    prob: infestation.prob,
    municipality: randomMunicipality(),
    area: randomNumber(10.0, 500.0, 2),
    scientific_name: plague.scientific,
    plague_name: plague.name,
    ugb: randomUGB(),
    id_projeto: randomIdProjeto()
  };
};

/**
 * Gera previsões mockadas
 * @returns {Object} Objeto com previsões por região e data
 */
export const generateMockPredictions = () => {
  const predictionsNorte = [];
  const predictionsSul = [];

  // Gerar 15 previsões para Norte
  for (let i = 0; i < 15; i++) {
    predictionsNorte.push(generateMockPrediction("Norte", i));
  }

  // Gerar 21 previsões para Sul
  for (let i = 0; i < 21; i++) {
    predictionsSul.push(generateMockPrediction("Sul", i));
  }

  const result = {
    dados: {
      "Norte": predictionsNorte,
      "Sul": predictionsSul
    },
    datePrediction: new Date().toISOString()
  };

  logger.debug("Previsões mock geradas", {
    norte: predictionsNorte.length,
    sul: predictionsSul.length
  });

  return result;
};

/**
 * Gera dados de plots mock formatados como resposta da API
 * @returns {Object} Objeto com plots por região
 */
export const generateMockPlotsResponse = () => {
  const plotsNorte = generateMockPlots("Norte");
  const plotsSul = generateMockPlots("Sul");

  return {
    dados: {
      "Norte": plotsNorte,
      "Sul": plotsSul
    }
  };
};

